import React from "react";
import SettingsTabsHeader from "@/components/settings/SettingsTabsHeader";
import { strategies } from "./strategyData";
import { ASSET_COLORS } from "@/theme/tokens/colors";
import StrategyPieChart from "./StrategyPieChart";
import { Scale, BanknoteArrowUp, Rocket, ShieldHalf } from "lucide-react";

function buildChartData(allocation: { stocks: number; bonds: number; cash: number; alts: number; }) {
  return [
    { name: "Stocks", value: allocation.stocks, color: ASSET_COLORS.equity },
    { name: "Bonds", value: allocation.bonds, color: ASSET_COLORS.bonds },
    { name: "Cash", value: allocation.cash, color: ASSET_COLORS.cash },
    { name: "Alternatives", value: allocation.alts, color: ASSET_COLORS.alternatives },
  ].filter(s => s.value > 0);
}

export const StrategyTabs: React.FC = () => {
  const [activeId, setActiveId] = React.useState(strategies[0]?.id ?? "diversification");
  const tabs = strategies.map((s) => ({ id: s.id, label: s.label.toUpperCase() }));
  const ICONS = {
    diversification: Scale,
    income: BanknoteArrowUp,
    growth: Rocket,
    preservation: ShieldHalf,
  } as const;

  const active = strategies.find((s) => s.id === activeId)!;
  const Icon = ICONS[active.id as keyof typeof ICONS];

  return (
    <div className="w-full">
      {/* Interactive tabs - hidden in print */}
      <div className="no-print">
        <div className="mb-4">
          <SettingsTabsHeader
            tabs={tabs as any}
            activeTab={activeId as any}
            onTabChange={(v: any) => setActiveId(v)}
            activeTextClassName="text-blue-600 hover:text-blue-700"
          />
        </div>

        <div className="prose prose-slate max-w-none space-y-4 md:space-y-6">
          <div className="flex items-center gap-2">
            <Icon className="h-5 w-5 text-foreground" aria-hidden="true" />
            <h3 className="m-0 text-2xl font-semibold text-foreground">{active.title}</h3>
          </div>
          <p className="m-0 text-[15px] leading-7 text-foreground"><span className="font-medium">Objective:</span> {active.description}</p>

          <div className="md:grid md:grid-cols-[280px_1fr] md:gap-8 items-start">
            <div className="w-[220px] md:w-[260px] mx-auto md:mx-0">
              <StrategyPieChart data={buildChartData(active.allocation)} />
            </div>
            <div className="mt-4 md:mt-0">
              <h4 className="m-0 text-[16px] font-medium text-foreground">Typically, an investor seeking {active.label.toLowerCase()} is:</h4>
              <ul className="mt-3 space-y-2.5 list-disc pl-5 text-[15px] leading-7 text-foreground">
                {active.points.map((p, i) => (
                  <li key={i}>{p}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Print-only sequential view of all strategies */}
      <div className="print-only">
        <div className="prose prose-slate max-w-none space-y-8">
          {strategies.map((s) => {
            const PIcon = ICONS[s.id as keyof typeof ICONS];
            return (
              <section key={s.id} className="strategy-print-section">
                <div className="flex items-center gap-2">
                  <PIcon className="h-5 w-5 text-foreground" aria-hidden="true" />
                  <h3 className="m-0 text-2xl font-semibold text-foreground">{s.title}</h3>
                </div>
                <p className="m-0 text-[15px] leading-7 text-foreground"><span className="font-medium">Objective:</span> {s.description}</p>
                <div className="md:grid md:grid-cols-[280px_1fr] md:gap-8 items-start">
                  <div className="w-[220px] md:w-[260px] mx-auto md:mx-0 strategy-chart">
                    <StrategyPieChart data={buildChartData(s.allocation)} fixed width={260} height={240} inlineLegend />
                  </div>
                  <div className="mt-4 md:mt-0">
                    <h4 className="m-0 text-[16px] font-medium text-foreground">Typically, an investor seeking {s.label.toLowerCase()} is:</h4>
                    <ul className="mt-3 space-y-2.5 list-disc pl-5 text-[15px] leading-7 text-foreground">
                      {s.points.map((p, i) => (
                        <li key={i}>{p}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </section>
            );
          })}
        </div>
      </div>
    </div>
  );
}
;

export default StrategyTabs;
